import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addSiteContact,
  deleteSiteContact,
  updateSiteContact,
  getSiteContacts,
  ProjectContactDto,
  searchSiteContact,
} from '../siteApi';

interface UseSiteContactsOptions {
  pageSize?: number;
  pageNumber?: number;
  enabled?: boolean;
}

export const useSiteContacts = (
  customerId: string,
  projectId: string,
  options: UseSiteContactsOptions = {}
) => {
  const { pageSize = 100, pageNumber = 1, enabled = true } = options;

  return useQuery({
    queryKey: ['site-contacts', projectId, customerId, { pageSize, pageNumber }],
    queryFn: async () => {
      const response = await getSiteContacts(customerId, projectId, {
        pageSize,
        pageNumber,
      });
      return response || [];
    },
    enabled: enabled && Boolean(customerId) && Boolean(projectId),
  });
};

export const useAddSiteContact = (customerId: string, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactData: ProjectContactDto) => {
      const response = await addSiteContact(customerId, projectId, contactData);
      return response;
    },
    onSuccess: () => {
      // Invalidate the contacts query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['site-contacts', projectId, customerId] });
    },
  });
};

export const useDeleteSiteContact = (customerId: string, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactId: string) => {
      await deleteSiteContact(customerId, projectId, contactId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-contacts', projectId, customerId] });
    },
  });
};

export const useUpdateSiteContact = (customerId: string, projectId: string, contactId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactData: ProjectContactDto) => {
      const response = await updateSiteContact(customerId, projectId, contactId, contactData);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-contacts', projectId, customerId] });
    },
  });
};

export const useSiteContactsSearch = (search?: string) => {
  return useQuery({
    queryKey: ['site-contacts-search', search],
    queryFn: async () => {
      const response = await searchSiteContact(search);
      return response || [];
    },
  });
};
